import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  LoadingState,
  PageHeader,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui'
import { createFileRoute, Link } from '@tanstack/react-router'
import { GaugeIcon, SmilePlusIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useFinalizationReasons } from '@/hooks/use-attendances'
import { useAuth } from '@/hooks/use-auth'
import {
  type NpsResponseFilters,
  useNpsMetrics,
  useNpsResponses,
} from '@/hooks/use-nps'
import type { NpsBucket, NpsResponse } from '@/types/nps'

export const Route = createFileRoute('/_app/nps')({
  component: NpsPage,
})

const ALL = '__all__'

const BUCKET_LABEL: Record<NpsBucket, string> = {
  promoter: 'Promotor',
  passive: 'Neutro',
  detractor: 'Detrator',
}

const BUCKET_VARIANT: Record<NpsBucket, 'success' | 'warning' | 'destructive'> =
  {
    promoter: 'success',
    passive: 'warning',
    detractor: 'destructive',
  }

const BUCKET_FILTERS: { label: string; value: string }[] = [
  { label: 'Todas', value: 'all' },
  { label: 'Promotores', value: 'promoter' },
  { label: 'Neutros', value: 'passive' },
  { label: 'Detratores', value: 'detractor' },
]

function NpsPage() {
  const { currentOrgId } = useAuth()
  const { data: reasons = [] } = useFinalizationReasons(currentOrgId)

  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [reasonId, setReasonId] = useState(ALL)
  const [bucket, setBucket] = useState('all')
  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
  }, [from, to, reasonId, bucket])

  const baseFilters = {
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(reasonId !== ALL ? { reasonId } : {}),
  }
  const responseFilters: NpsResponseFilters = {
    ...baseFilters,
    ...(bucket !== 'all' ? { bucket: bucket as NpsBucket } : {}),
    page,
  }

  const { data: metrics } = useNpsMetrics(currentOrgId, baseFilters)
  const { data: responsesResp, isLoading } = useNpsResponses(
    currentOrgId,
    responseFilters
  )

  const responses = responsesResp?.data ?? []
  const total = responsesResp?.meta?.total ?? responsesResp?.total ?? 0
  const limit = responsesResp?.meta?.limit ?? responsesResp?.limit ?? 20
  const totalPages = Math.max(1, Math.ceil(total / limit))

  const hasFilters = !!from || !!to || reasonId !== ALL

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        icon={SmilePlusIcon}
        title="Pesquisa de satisfação (NPS)"
        description="Notas e comentários enviados pelos clientes após o atendimento"
      />

      <main className="min-h-0 flex-1 space-y-6 overflow-auto p-6">
        {/* Filtros */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">De</Label>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-9 w-40"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Até</Label>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-9 w-40"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Motivo de finalização</Label>
            <Select value={reasonId} onValueChange={setReasonId}>
              <SelectTrigger className="h-9 w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos os motivos</SelectItem>
                {reasons.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {hasFilters ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFrom('')
                setTo('')
                setReasonId(ALL)
              }}
            >
              Limpar
            </Button>
          ) : null}
        </div>

        {/* KPIs */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="py-4">
              <p className="text-muted-foreground text-xs">NPS</p>
              <p className="text-3xl font-semibold tabular-nums">
                {metrics?.nps ?? 0}
              </p>
              <p className="text-muted-foreground text-xs">
                Nota média {metrics?.averageScore ?? 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <p className="text-muted-foreground text-xs">Taxa de resposta</p>
              <p className="text-3xl font-semibold tabular-nums">
                {metrics?.responseRate ?? 0}%
              </p>
              <p className="text-muted-foreground text-xs">
                {metrics?.responded ?? 0} de {metrics?.sent ?? 0} enviadas
              </p>
            </CardContent>
          </Card>
          <Card className="sm:col-span-2">
            <CardContent className="space-y-2 py-4">
              <p className="text-muted-foreground text-xs">Distribuição</p>
              <BucketBar
                promoters={metrics?.promoters ?? 0}
                passives={metrics?.passives ?? 0}
                detractors={metrics?.detractors ?? 0}
              />
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                <Legend
                  color="bg-success"
                  label="Promotores"
                  value={metrics?.promoters ?? 0}
                />
                <Legend
                  color="bg-warning"
                  label="Neutros"
                  value={metrics?.passives ?? 0}
                />
                <Legend
                  color="bg-destructive"
                  label="Detratores"
                  value={metrics?.detractors ?? 0}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Timeline */}
        {metrics && metrics.timeline.length > 0 ? (
          <Card>
            <CardContent className="py-4">
              <p className="text-muted-foreground mb-3 text-xs">
                Respostas por dia
              </p>
              <Timeline data={metrics.timeline} />
            </CardContent>
          </Card>
        ) : null}

        {/* Filtro por bucket */}
        <div className="bg-card border-border flex w-fit items-center gap-1 rounded-md border p-1">
          {BUCKET_FILTERS.map((filter) => {
            const active = bucket === filter.value
            return (
              <button
                key={filter.value}
                type="button"
                onClick={() => setBucket(filter.value)}
                className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {filter.label}
              </button>
            )
          })}
        </div>

        {/* Tabela de respostas */}
        {isLoading ? (
          <LoadingState />
        ) : responses.length === 0 ? (
          <Card>
            <CardContent className="text-muted-foreground flex flex-col items-center gap-2 py-12 text-sm">
              <GaugeIcon className="size-8 opacity-50" />
              Nenhuma resposta de NPS para os filtros selecionados.
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid gap-2">
              {responses.map((response) => (
                <ResponseRow key={response.id} response={response} />
              ))}
            </div>

            {totalPages > 1 ? (
              <div className="flex items-center justify-between">
                <p className="text-muted-foreground text-sm">
                  {(page - 1) * limit + 1}-{Math.min(page * limit, total)} de{' '}
                  {total}
                </p>
                <Pagination className="mx-0 w-auto">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        aria-disabled={page <= 1}
                        className={
                          page <= 1 ? 'pointer-events-none opacity-50' : ''
                        }
                        onClick={(e) => {
                          e.preventDefault()
                          setPage((p) => Math.max(1, p - 1))
                        }}
                      />
                    </PaginationItem>
                    {Array.from({ length: totalPages })
                      .map((_, i) => i + 1)
                      .filter(
                        (n) =>
                          n === 1 || n === totalPages || Math.abs(n - page) <= 1
                      )
                      .map((n) => (
                        <PaginationItem key={n}>
                          <PaginationLink
                            isActive={n === page}
                            onClick={(e) => {
                              e.preventDefault()
                              setPage(n)
                            }}
                          >
                            {n}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                    <PaginationItem>
                      <PaginationNext
                        aria-disabled={page >= totalPages}
                        className={
                          page >= totalPages
                            ? 'pointer-events-none opacity-50'
                            : ''
                        }
                        onClick={(e) => {
                          e.preventDefault()
                          setPage((p) => Math.min(totalPages, p + 1))
                        }}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            ) : null}
          </>
        )}
      </main>
    </div>
  )
}

function BucketBar({
  promoters,
  passives,
  detractors,
}: {
  promoters: number
  passives: number
  detractors: number
}) {
  const total = promoters + passives + detractors
  if (total === 0) {
    return <div className="bg-muted h-2.5 w-full rounded-full" />
  }
  const pct = (value: number) => `${(value / total) * 100}%`
  return (
    <div className="bg-muted flex h-2.5 w-full overflow-hidden rounded-full">
      <div className="bg-success" style={{ width: pct(promoters) }} />
      <div className="bg-warning" style={{ width: pct(passives) }} />
      <div className="bg-destructive" style={{ width: pct(detractors) }} />
    </div>
  )
}

function Legend({
  color,
  label,
  value,
}: {
  color: string
  label: string
  value: number
}) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`size-2 rounded-full ${color}`} />
      <span className="text-foreground font-medium">{value}</span>
      <span className="text-muted-foreground">{label}</span>
    </span>
  )
}

function Timeline({
  data,
}: {
  data: Array<{ date: string; responses: number; averageScore: number }>
}) {
  const max = Math.max(1, ...data.map((d) => d.responses))
  return (
    <div className="flex h-28 items-end gap-1">
      {data.map((point) => (
        <div
          key={point.date}
          className="flex flex-1 flex-col items-center gap-1"
          title={`${point.date}: ${point.responses} resposta(s), média ${point.averageScore}`}
        >
          <div
            className="bg-primary/70 w-full rounded-t"
            style={{ height: `${(point.responses / max) * 100}%` }}
          />
          <span className="text-muted-foreground text-2xs truncate">
            {point.date.slice(5)}
          </span>
        </div>
      ))}
    </div>
  )
}

function ResponseRow({ response }: { response: NpsResponse }) {
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-3 py-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium">
              {response.contact?.name ?? response.contact?.phone ?? 'Contato'}
            </span>
            {response.bucket ? (
              <Badge variant={BUCKET_VARIANT[response.bucket]}>
                {response.score} · {BUCKET_LABEL[response.bucket]}
              </Badge>
            ) : null}
          </div>
          {response.comment ? (
            <p className="text-muted-foreground text-sm">
              “{response.comment}”
            </p>
          ) : (
            <p className="text-muted-foreground/60 text-sm italic">
              Sem comentário
            </p>
          )}
          <p className="text-muted-foreground text-xs">
            {response.respondedAt
              ? new Date(response.respondedAt).toLocaleString('pt-BR')
              : ''}
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/inbox" search={{ id: response.conversationId }}>
            Ver atendimento
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}
